/**
 * Module #34 Phase A — shared chat surface (message list + input).
 *
 * Used by both CopilotPanel (slide-in) and AdminCopilot (/admin/copilot
 * full-page). Differs only in `compact` styling — both wrap useCopilot
 * and render the same conversation shape.
 */

import { BIDDER_PRESETS, BIDDER_PRESET_BLURBS, BIDDER_PRESET_LABELS } from '@auctisync/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  ConfirmDecision,
  InputDescriptor,
  PendingState,
  ReportAttachment,
  UseCopilotReturn,
} from '../../hooks/useCopilot';
import { expandHexForSwatch, initialCardValue, isCardValueComplete } from './copilotInputCard';
import { LogoUploader } from '../LogoUploader';
import type { DetectedColours } from '../../lib/logoColours';

export interface CopilotChatProps {
  copilot: UseCopilotReturn;
  /** Tighter spacing for the slide-in panel. */
  compact?: boolean;
}

export function CopilotChat({ copilot, compact = false }: CopilotChatProps) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [copilot.messages.length, copilot.status]);

  // Busy = waiting for the first token (sending) OR tokens flowing (streaming).
  // Both disable the composer; only `sending` shows the typing animation —
  // once `streaming`, the live text is its own progress indicator.
  const isSending = copilot.status.kind === 'sending';
  const isStreaming = copilot.status.kind === 'streaming';
  const isBusy = isSending || isStreaming;
  // While a Tier-2/3 action awaits confirmation, the card is the required next
  // action — lock the composer so the user resolves it first.
  const isAwaiting = copilot.status.kind === 'awaiting_confirmation';
  const composerDisabled = isBusy || isAwaiting;

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (draft.trim().length === 0 || composerDisabled) return;
    const toSend = draft;
    setDraft('');
    // Streaming send — final answer appears token-by-token. Falls back to a
    // clear error state if the stream can't open.
    void copilot.sendStreaming(toSend);
  }

  if (!copilot.configured) {
    return (
      <div
        style={{
          padding: '1.5rem',
          fontSize: '0.875rem',
          color: 'var(--text-muted)',
          textAlign: 'center',
        }}
      >
        Copilot is not configured for this environment. Set
        <code style={{ margin: '0 0.25em' }}>VITE_INTELLIGENCE_BASE_URL</code>
        in <code>apps/web/.env.local</code>, then refresh.
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: compact ? '1rem' : '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        {copilot.messages.length === 0 && <EmptyState compact={compact} />}
        {copilot.messages.map((m, idx) => (
          <MessageRow
            key={idx}
            role={m.role}
            text={m.text}
            attachments={m.attachments}
            compact={compact}
          />
        ))}
        {copilot.pending !== null && (
          <ConfirmationCard
            pending={copilot.pending}
            busy={isBusy}
            onResolve={(decisions) => void copilot.confirmAction(decisions)}
          />
        )}
        {isSending && <TypingRow compact={compact} />}
        {copilot.status.kind === 'error' && (
          <ErrorRow
            status={copilot.status.status}
            code={copilot.status.code}
            detail={copilot.status.detail}
          />
        )}
      </div>

      {copilot.usage !== null && (
        <UsageStrip
          turnsUsedToday={copilot.usage.turnsUsedToday}
          turnsLimitToday={copilot.usage.turnsLimitToday}
        />
      )}

      <form
        onSubmit={onSubmit}
        style={{
          padding: compact ? '0.75rem' : '1rem',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-elev)',
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-end',
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit(e as unknown as FormEvent<HTMLFormElement>);
            }
          }}
          placeholder={
            isAwaiting ? 'Confirm or cancel the action above to continue…' : 'Ask the copilot…'
          }
          rows={compact ? 2 : 3}
          disabled={composerDisabled}
          style={{
            flex: 1,
            resize: 'vertical',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontFamily: 'inherit',
          }}
        />
        <button
          type="submit"
          disabled={composerDisabled || draft.trim().length === 0}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: 'none',
            background:
              composerDisabled || draft.trim().length === 0
                ? 'var(--surface-muted)'
                : 'var(--system-brand-accent)',
            color:
              composerDisabled || draft.trim().length === 0
                ? 'var(--text-muted)'
                : 'var(--system-brand-accent-fg)',
            fontWeight: 600,
            cursor: composerDisabled || draft.trim().length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {isSending ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

function EmptyState({ compact }: { compact: boolean }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: compact ? '2rem 1rem' : '3rem 1.5rem',
        color: 'var(--text-muted)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>
        Welcome to your copilot
      </div>
      <div style={{ fontSize: '0.875rem', maxWidth: '24rem', margin: '0 auto', lineHeight: 1.5 }}>
        Ask me to look up your auctions, bidders, invoices, and sales history; draft bidder emails
        and reports; manage lots; or surface what needs your attention — and I'll remember how you
        work across conversations.
      </div>
    </div>
  );
}

/** Decode a UTF-8 base64 CSV and trigger a browser download. */
function downloadCsv(csvBase64: string, filename: string): void {
  const binary = atob(csvBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DownloadButton({ attachment }: { attachment: ReportAttachment }) {
  const rows = attachment.row_count;
  const rowLabel = typeof rows === 'number' ? ` · ${rows} row${rows === 1 ? '' : 's'}` : '';
  return (
    <button
      type="button"
      onClick={() => downloadCsv(attachment.csv_base64, attachment.filename)}
      title={`Download ${attachment.filename}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4375rem',
        padding: '0.4375rem 0.75rem',
        borderRadius: '0.5rem',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        color: 'var(--system-brand-accent)',
        fontSize: '0.8125rem',
        fontWeight: 600,
        cursor: 'pointer',
        maxWidth: '100%',
      }}
    >
      <span aria-hidden="true">⬇</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {attachment.filename}
        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{rowLabel}</span>
      </span>
    </button>
  );
}

function MessageRow({
  role,
  text,
  attachments,
  compact,
}: {
  role: 'user' | 'assistant';
  text: string;
  attachments?: ReportAttachment[] | undefined;
  compact: boolean;
}) {
  const isUser = role === 'user';
  const hasDownloads = !isUser && attachments !== undefined && attachments.length > 0;
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.375rem',
          maxWidth: compact ? '85%' : '70%',
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        {text.length > 0 && (
          <div
            style={{
              padding: '0.5rem 0.875rem',
              borderRadius: '0.75rem',
              background: isUser ? 'var(--system-brand-accent)' : 'var(--surface-elev)',
              color: isUser ? 'var(--system-brand-accent-fg)' : 'var(--text)',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              border: isUser ? 'none' : '1px solid var(--border)',
              lineHeight: 1.5,
            }}
          >
            {text}
          </div>
        )}
        {hasDownloads && attachments.map((att, i) => <DownloadButton key={i} attachment={att} />)}
      </div>
    </div>
  );
}

function TypingRow({ compact }: { compact: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div
        style={{
          padding: '0.5rem 0.875rem',
          borderRadius: '0.75rem',
          background: 'var(--surface-elev)',
          color: 'var(--text-muted)',
          fontSize: compact ? '0.8125rem' : '0.875rem',
          border: '1px solid var(--border)',
        }}
      >
        Thinking…
      </div>
    </div>
  );
}

function ErrorRow({ status, code, detail }: { status: number; code: string; detail: string }) {
  return (
    <div
      style={{
        padding: '0.75rem 1rem',
        borderRadius: '0.5rem',
        background: 'var(--danger-bg, #fef2f2)',
        color: 'var(--danger-text, #991b1b)',
        fontSize: '0.8125rem',
        border: '1px solid var(--danger-border, #fecaca)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
        {status > 0 ? `Error ${status}` : 'Error'} · {code}
      </div>
      <div>{detail}</div>
    </div>
  );
}

function UsageStrip({
  turnsUsedToday,
  turnsLimitToday,
}: {
  turnsUsedToday: number;
  turnsLimitToday: number;
}) {
  const unmetered = turnsLimitToday < 0;
  const pct = unmetered ? 0 : Math.min(100, Math.round((turnsUsedToday / turnsLimitToday) * 100));
  const warning = pct >= 75;
  return (
    <div
      style={{
        padding: '0.375rem 0.875rem',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface-elev)',
        fontSize: '0.6875rem',
        color: warning ? 'var(--danger-text, #991b1b)' : 'var(--text-muted)',
        textAlign: 'right',
      }}
    >
      {unmetered
        ? `${turnsUsedToday} turns today · unmetered tier`
        : `${turnsUsedToday} / ${turnsLimitToday} turns today (${pct}%)`}
    </div>
  );
}

/** Mirror of the backend's confirmationMatches normalization (whitespace +
 *  case-insensitive) so the Confirm button enables exactly when the tool will
 *  accept the typed phrase. */
function normalizeConfirmation(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Inline confirmation card (chunk 5d). Renders the held Tier-2/3 actions and
 * gates firing them on a human decision: Tier-2 is a Confirm click; Tier-3
 * also requires typing the exact challenge phrase. Resolving sends every
 * action's decision to confirmAction() in one call.
 */
function ConfirmationCard({
  pending,
  busy,
  onResolve,
}: {
  pending: PendingState;
  busy: boolean;
  onResolve: (decisions: ConfirmDecision[]) => void;
}) {
  const [phrases, setPhrases] = useState<Record<string, string>>({});
  // 'input'-tier value cards (onboarding C2): the owner's editable value per
  // action, seeded from each descriptor's `initial`.
  const [values, setValues] = useState<Record<string, Record<string, unknown>>>(() => {
    const seed: Record<string, Record<string, unknown>> = {};
    for (const a of pending.actions) {
      if (a.tier === 'input' && a.input_descriptor) {
        seed[a.tool_use_id] = initialCardValue(a.input_descriptor);
      }
    }
    return seed;
  });
  // A logo upload in flight for any card blocks Confirm — otherwise a fast
  // Confirm would save branding before the logo URL lands in the value.
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const anyUploading = Object.values(uploading).some(Boolean);
  const danger = pending.actions.some((a) => a.tier === 'tier3');

  const allPhrasesMatch = pending.actions.every(
    (a) =>
      a.tier !== 'tier3' ||
      normalizeConfirmation(phrases[a.tool_use_id] ?? '') ===
        normalizeConfirmation(a.confirmation_phrase ?? ''),
  );
  const allInputComplete = pending.actions.every(
    (a) =>
      a.tier !== 'input' ||
      (a.input_descriptor !== undefined &&
        isCardValueComplete(a.input_descriptor, values[a.tool_use_id] ?? {})),
  );
  const canConfirm = !busy && !anyUploading && allPhrasesMatch && allInputComplete;

  function confirm(): void {
    onResolve(
      pending.actions.map((a) => ({
        tool_use_id: a.tool_use_id,
        confirmed: true,
        ...(a.tier === 'tier3' && { confirmation_text: phrases[a.tool_use_id] ?? '' }),
        ...(a.tier === 'input' && { value: values[a.tool_use_id] ?? {} }),
      })),
    );
  }
  function cancel(): void {
    onResolve(pending.actions.map((a) => ({ tool_use_id: a.tool_use_id, confirmed: false })));
  }

  return (
    <div
      role="group"
      aria-label="Confirm action"
      style={{
        border: `1px solid ${danger ? 'var(--danger-border, #fecaca)' : 'var(--border)'}`,
        background: danger ? 'var(--danger-bg, #fef2f2)' : 'var(--surface-elev)',
        borderRadius: '0.75rem',
        padding: '0.875rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div
        style={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: danger ? 'var(--danger-text, #991b1b)' : 'var(--text-muted)',
        }}
      >
        {pending.actions.length > 1 ? 'Confirm these actions' : 'Confirm this action'}
      </div>

      {pending.actions.map((a) => (
        <div
          key={a.tool_use_id}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.9375rem' }}>
            {a.title}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {a.description}
          </div>
          {a.tier === 'tier3' && a.confirmation_phrase !== undefined && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                marginTop: '0.25rem',
              }}
            >
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Type{' '}
                <code
                  style={{
                    background: 'var(--surface-muted, #f1f5f9)',
                    padding: '0.0625rem 0.25rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    color: 'var(--text)',
                  }}
                >
                  {a.confirmation_phrase}
                </code>{' '}
                to confirm
              </label>
              <input
                type="text"
                value={phrases[a.tool_use_id] ?? ''}
                onChange={(e) => setPhrases((p) => ({ ...p, [a.tool_use_id]: e.target.value }))}
                disabled={busy}
                placeholder={a.confirmation_phrase}
                aria-label={`Type "${a.confirmation_phrase}" to confirm`}
                autoComplete="off"
                style={{
                  padding: '0.4375rem 0.625rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.5rem',
                  fontSize: '0.8125rem',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          )}
          {a.tier === 'input' && a.input_descriptor && (
            <InputCardControl
              descriptor={a.input_descriptor}
              value={values[a.tool_use_id] ?? {}}
              // Functional update: an async writer (the logo upload) merges
              // against the LATEST value, so a color the owner edits mid-upload
              // is never clobbered by the upload's stale closure.
              onChange={(update) =>
                setValues((s) => ({ ...s, [a.tool_use_id]: update(s[a.tool_use_id] ?? {}) }))
              }
              onUploadingChange={(u) => setUploading((s) => ({ ...s, [a.tool_use_id]: u }))}
              busy={busy}
            />
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          style={{
            padding: '0.4375rem 0.875rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.8125rem',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!canConfirm}
          style={{
            padding: '0.4375rem 0.875rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: canConfirm
              ? danger
                ? 'var(--danger-text, #991b1b)'
                : 'var(--system-brand-accent)'
              : 'var(--surface-muted)',
            color: canConfirm ? '#fff' : 'var(--text-muted)',
            fontWeight: 600,
            fontSize: '0.8125rem',
            cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? '…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

/** The value control for an 'input'-tier card (onboarding C2). Renders per
 * descriptor kind; reports the whole value object up so the card can gate
 * Confirm + send it as the decision's `value`. */
function InputCardControl({
  descriptor,
  value,
  onChange,
  onUploadingChange,
  busy,
}: {
  descriptor: InputDescriptor;
  value: Record<string, unknown>;
  onChange: (update: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  onUploadingChange: (uploading: boolean) => void;
  busy: boolean;
}) {
  // Merge against the latest value (functional), never a captured snapshot —
  // the logo upload resolves asynchronously and must not revert concurrent edits.
  const set = (k: string, v: string) => onChange((prev) => ({ ...prev, [k]: v }));
  if (descriptor.kind === 'branding') {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}
      >
        {/* THREE colours, matching the Branding admin page field for field.
            Onboarding asked for two and never mentioned the third, so a house
            only met "Dark colour" if they later went looking for the admin
            page — and most never did. */}
        <ColorField
          label="Primary colour"
          hint="The band across the top of every page, and the colour your site is built from."
          value={value['primary_color']}
          onChange={(v) => set('primary_color', v)}
          busy={busy}
        />
        <ColorField
          label="Accent colour"
          hint="Your buttons. Leave blank to use your main colour."
          value={value['accent_color']}
          onChange={(v) => set('accent_color', v)}
          busy={busy}
        />
        <ColorField
          label="Secondary colour"
          hint="Panels like the sale header. Blank means we darken your main colour for you."
          value={value['dark_color']}
          onChange={(v) => set('dark_color', v)}
          busy={busy}
        />
        <LogoDropzone
          logoUrl={value['logo_url']}
          onLogoUrl={(url) => set('logo_url', url)}
          onUploadingChange={onUploadingChange}
          busy={busy}
          /* 🔴 FILLS BLANKS ONLY. A house that has already typed a hex chose it
             on purpose, and a logo upload quietly rewriting it is worse than
             never detecting anything — Jim: "They can specify different ones
             after that." Reads from `prev` inside the updater rather than the
             rendered `value`, because the three writes land in one batch and the
             second would otherwise decide against a stale snapshot. */
          onColoursDetected={(c) => {
            onChange((prev) => {
              const blank = (k: string) => typeof prev[k] !== 'string' || prev[k].trim() === '';
              return {
                ...prev,
                ...(blank('primary_color') && { primary_color: c.primary }),
                ...(blank('accent_color') && { accent_color: c.accent }),
                ...(blank('dark_color') && { dark_color: c.dark }),
              };
            });
          }}
        />
        <TextField
          label="Logo URL (or paste one)"
          value={value['logo_url']}
          onChange={(v) => set('logo_url', v)}
          busy={busy}
          placeholder="https://…"
        />
        {/* The look. Optional — leaving it alone keeps `modern`, which is what
            every house had before this control existed. Type and shape only:
            it cannot change a colour or move a box, so it is safe to offer
            during setup rather than holding it back for an admin page. */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Look (optional — you can change this later in Branding)
          </span>
          <select
            value={typeof value['preset'] === 'string' ? value['preset'] : ''}
            onChange={(e) => {
              set('preset', e.target.value);
            }}
            disabled={busy}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--admin-hairline, var(--border))',
              borderRadius: 'var(--r-tile, 0.375rem)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
            }}
          >
            <option value="">Modern (default)</option>
            {BIDDER_PRESETS.filter((p) => p !== 'modern').map((p) => (
              <option key={p} value={p}>
                {BIDDER_PRESET_LABELS[p]} — {BIDDER_PRESET_BLURBS[p]}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }
  if (descriptor.kind === 'domain') {
    return (
      <div style={{ marginTop: '0.25rem' }}>
        <TextField
          label="Custom domain"
          value={value['domain']}
          onChange={(v) => set('domain', v)}
          busy={busy}
          placeholder="bids.yourhouse.com"
        />
      </div>
    );
  }
  return null;
}

function ColorField({
  label,
  hint,
  value,
  onChange,
  busy,
}: {
  label: string;
  /**
   * What this colour actually DOES, in the house's own terms.
   *
   * 🔴 THE HINTS ARE THE POINT OF THIS FIELD, not decoration. Onboarding used to
   * offer "Primary color" and "Accent color (optional)" with nothing else, so a
   * house choosing colours here was never told that the accent paints every
   * button in their app. Discount Tool Auctions picked a pale yellow and got
   * pale yellow buttons — behaving exactly as designed, and a surprise to
   * everyone. The admin branding page has always carried these explanations;
   * the surface where the choice is actually MADE did not.
   *
   * Wording is copied from `AdminBranding.tsx` deliberately. Two screens asking
   * for the same value in different words is the wording half of the
   * no-mechanical-divergence rule.
   */
  hint: string;
  value: unknown;
  onChange: (v: string) => void;
  busy: boolean;
}) {
  const hex = typeof value === 'string' ? value : '';
  const swatch = expandHexForSwatch(value) ?? '#2264dc';
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
      }}
    >
      <span style={{ minWidth: '9rem' }}>
        {label}
        <span style={{ display: 'block', opacity: 0.75, fontSize: '0.7rem', lineHeight: 1.3 }}>
          {hint}
        </span>
      </span>
      <input
        type="color"
        value={swatch}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        aria-label={`${label} swatch`}
        style={{
          width: '2rem',
          height: '1.75rem',
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          background: 'transparent',
          cursor: busy ? 'not-allowed' : 'pointer',
          padding: 0,
        }}
      />
      <input
        type="text"
        value={hex}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        placeholder="#2264dc"
        aria-label={label}
        autoComplete="off"
        style={{
          flex: 1,
          padding: '0.375rem 0.5rem',
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          fontSize: '0.8125rem',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  busy,
  placeholder,
}: {
  label: string;
  value: unknown;
  onChange: (v: string) => void;
  busy: boolean;
  placeholder?: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
      }}
    >
      {label}
      <input
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
        style={{
          padding: '0.4375rem 0.625rem',
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          fontSize: '0.8125rem',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

/**
 * The branding card's logo field.
 *
 * 🔴 THE UPLOADER ITSELF NOW LIVES IN `components/LogoUploader.tsx`, shared with
 * Admin → Branding, which gained the ability to CHANGE a logo on 2026-09-14. It
 * was defined here and only here until then; a second copy on that page would
 * have meant two statements of the allowed types and the size ceiling.
 *
 * No `tenantId` is passed: a house being onboarded is the only one its owner
 * has, so the Edge Function resolves it without help. Admin → Branding, where an
 * owner may hold several, does pass it.
 */
function LogoDropzone({
  logoUrl,
  onLogoUrl,
  onUploadingChange,
  busy,
  onColoursDetected,
}: {
  logoUrl: unknown;
  onLogoUrl: (url: string) => void;
  onUploadingChange: (uploading: boolean) => void;
  busy: boolean;
  /** Forwarded straight through — see `LogoUploader` for why these are
   *  suggestions the caller applies, not values it assigns. */
  onColoursDetected?: (c: DetectedColours) => void;
}) {
  return (
    <LogoUploader
      logoUrl={typeof logoUrl === 'string' ? logoUrl : null}
      onLogoUrl={onLogoUrl}
      onUploadingChange={onUploadingChange}
      busy={busy}
      /* Spread rather than passed: `exactOptionalPropertyTypes` distinguishes
         "absent" from "present and undefined", so forwarding an unset optional
         directly is a type error. */
      {...(onColoursDetected !== undefined && { onColoursDetected })}
    />
  );
}
